import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { BarChart3, Crown, Loader2, Save, Target, Sparkles, TrendingUp, Users, RefreshCw } from 'lucide-react';
import { ScoresSection, TitleVariations, ImprovementsGrid, CtaSection, MaxExtrasSection } from './AnalyzerResults';

const PLATFORMS = ['YouTube', 'Instagram', 'TikTok'];

const ContentAnalyzer = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', script: '', platform: '', niche: ''
  });

  const userPlan = user?.subscriptionPlan;
  const isPro = userPlan === 'pro' || userPlan === 'max';
  const isMax = userPlan === 'max';
  const hasAccess = isPro;
  const creditCost = isMax ? 20 : 15;

  // Load the latest analysis on mount
  useEffect(() => {
    const loadLatestAnalysis = async () => {
      if (!hasAccess) {
        setLoadingLatest(false);
        return;
      }
      try {
        const res = await api.get('/analyzer/latest');
        if (res.data) {
          // Restore the form input and result from the saved analysis
          setForm(res.data.input || { title: '', description: '', script: '', platform: '', niche: '' });
          setResult({
            pro: res.data.proResult,
            maxExtras: res.data.maxExtras,
            creditsUsed: res.data.creditsUsed
          });
        }
      } catch (err) {
        // No saved analysis or error - that's fine
        console.log('No previous analysis found');
      } finally {
        setLoadingLatest(false);
      }
    };
    loadLatestAnalysis();
  }, [api, hasAccess]);

  if (!hasAccess) {
    return <LockedState navigate={navigate} />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.script || !form.platform || !form.niche) {
      toast.error('All fields are required.');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/analyzer/analyze', form);
      setResult(res.data);
      toast.success(`Analysis complete! ${res.data.creditsUsed} credits used.`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Analysis failed.';
      toast.error(typeof msg === 'string' ? msg : 'Analysis failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!result) return;
    setSaving(true);
    try {
      // Save the complete analysis with all data
      await api.post('/saved', {
        type: 'analysis',
        title: `Analysis: ${form.title}`,
        content: {
          input: form,
          scores: result.pro.scores,
          titleVariations: result.pro.titleVariations,
          improvedDescription: result.pro.improvedDescription,
          betterHook: result.pro.betterHook,
          ctaRecommendation: result.pro.ctaRecommendation,
          maxExtras: result.maxExtras || null
        }
      });
      toast.success('Analysis saved to library!');
    } catch (err) {
      toast.error('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = async () => {
    // Clear the analysis from the database so it doesn't reload
    try {
      await api.delete('/analyzer/latest');
    } catch (err) {
      // Ignore errors - just clear locally
    }
    setResult(null);
    setForm({ title: '', description: '', script: '', platform: '', niche: '' });
  };

  // Show loading state while fetching latest analysis
  if (loadingLatest) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-analyzer">
      <AnalyzerHeader creditCost={creditCost} isMax={isMax} />

      {!result && (
        <AnalyzerForm
          form={form}
          setForm={setForm}
          loading={loading}
          creditCost={creditCost}
          onSubmit={handleSubmit}
        />
      )}

      {result && (
        <div className="space-y-5" data-testid="analyzer-results">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={resetForm}
              data-testid="analyzer-new-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              New Analysis
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="analyzer-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save to Library
            </Button>
          </div>

          <ScoresSection scores={result.pro.scores} />
          <TitleVariations titles={result.pro.titleVariations} />
          <ImprovementsGrid description={result.pro.improvedDescription} hook={result.pro.betterHook} />
          <CtaSection cta={result.pro.ctaRecommendation} />
          {result.maxExtras && <MaxExtrasSection maxExtras={result.maxExtras} />}
        </div>
      )}
    </div>
  );
};

const AnalyzerHeader = ({ creditCost, isMax }) => (
  <div>
    <div className="flex items-center gap-4">
      <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
        AI Content Analyzer
      </h1>
      <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 px-3 py-1">
        <Crown className="w-3.5 h-3.5 mr-1" /> PRO
      </Badge>
    </div>
    <p className="text-slate-400 mt-2">
      Deep AI analysis of your content before you publish.
      <span className="text-slate-500 ml-2">
        Cost: <span className="text-violet-400 font-semibold">{creditCost} credits</span> per analysis
        {isMax && <span className="text-cyan-400 text-xs ml-1">(includes Max insights)</span>}
      </span>
    </p>
  </div>
);

const AnalyzerForm = ({ form, setForm, loading, creditCost, onSubmit }) => {
  const inputClass = "w-full px-4 py-2.5 bg-slate-900/60 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50";

  return (
    <Card className="bg-slate-800/30 border-slate-700/50">
      <CardContent className="p-6">
        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Video / Post Title *</label>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. 5 Morning Habits That Changed My Life" className={inputClass} data-testid="analyzer-input-title" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Description *</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Your video/post description..." rows={3} className={`${inputClass} resize-none`} data-testid="analyzer-input-description" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Script / Transcript *</label>
            <textarea value={form.script} onChange={(e) => setForm({ ...form, script: e.target.value })} placeholder="Paste your full script or transcript here..." rows={6} className={`${inputClass} resize-none`} data-testid="analyzer-input-script" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Target Platform *</label>
              <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className={inputClass} data-testid="analyzer-input-platform">
                <option value="" className="bg-slate-900">Select platform</option>
                {PLATFORMS.map(p => <option key={p} value={p} className="bg-slate-900">{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Niche / Topic *</label>
              <input type="text" value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })} placeholder="e.g. fitness, tech, cooking" className={inputClass} data-testid="analyzer-input-niche" />
            </div>
          </div>
          <div className="flex items-center justify-between pt-2">
            <span className="text-sm text-slate-500">This will use <span className="text-violet-400 font-semibold">{creditCost} credits</span></span>
            <Button type="submit" disabled={loading} className="bg-violet-500 hover:bg-violet-600 text-white px-8" data-testid="analyzer-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</> : <><BarChart3 className="w-4 h-4 mr-2" /> Analyze Content</>}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const LockedBenefitCard = ({ icon: Icon, color, title, desc }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40">
    <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <p className="text-white font-medium text-sm">{title}</p>
      <p className="text-slate-500 text-xs">{desc}</p>
    </div>
  </div>
);

const LockedState = ({ navigate }) => (
  <div className="space-y-6" data-testid="analyzer-locked">
    <div>
      <div className="flex items-center gap-4">
        <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">AI Content Analyzer</h1>
        <div className="flex items-center gap-2 px-4 py-2 bg-violet-500 rounded-xl">
          <Crown className="w-5 h-5 text-white" />
          <span className="font-bold text-white">PRO</span>
        </div>
      </div>
      <p className="text-slate-400 mt-3 text-lg">Deep AI analysis of your content before you publish</p>
    </div>
    <Card className="bg-slate-800/50 border-slate-700/50">
      <CardContent className="p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center">
            <BarChart3 className="w-8 h-8 text-violet-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Upgrade to Pro Creator</h2>
          <p className="text-slate-400 max-w-md mx-auto">Unlock AI-powered content analysis with hook scoring, SEO insights, click-through predictions, and actionable rewrites.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-8">
          <LockedBenefitCard icon={Target} color="bg-violet-500/15 text-violet-400" title="Score every dimension" desc="Hook, SEO, CTR, readability in one click" />
          <LockedBenefitCard icon={Sparkles} color="bg-cyan-500/15 text-cyan-400" title="AI-rewritten titles" desc="3 optimized variations per analysis" />
          <LockedBenefitCard icon={TrendingUp} color="bg-emerald-500/15 text-emerald-400" title="Viral potential (Max)" desc="Retention prediction and trending analysis" />
          <LockedBenefitCard icon={Users} color="bg-amber-500/15 text-amber-400" title="Audience profiling (Max)" desc="Know who your content will reach" />
        </div>
        <div className="text-center">
          <Button onClick={() => navigate('/billing')} className="bg-gradient-to-r from-violet-500 to-cyan-500 hover:opacity-90 text-white" data-testid="analyzer-upgrade-btn">
            <Crown className="w-4 h-4 mr-2" /> Upgrade to Pro Creator - $19/month
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
);

export default ContentAnalyzer;
